import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Layers, Package, ShieldCheck, Stethoscope, Volume2, VolumeX, Waves, Wind } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fileUrl } from "@/lib/api";
import { BRAND, DESTAQUES, MATERIAIS, MISSAO, RESULTADO, VISAO, waLink } from "@/lib/content";
import { SectionTitle } from "@/components/site/PageHero";
import { WhatsAppIcon } from "@/components/site/WhatsAppFloat";

const ICONS = [VolumeX, Gauge, Waves, Wind];
const AREAS = [
  { to: "/tratamento-de-ruidos", n: "12", t: "Tratamento de Ruídos", d: "Diagnóstico e tratamento de ruídos, vibrações e reverberações.", I: Volume2 },
  { to: "/isolamento-termoacustico", n: "09", t: "Isolamento Termoacústico", d: "Isolamento termoacústico por níveis, do capô à vedação de vãos.", I: Layers },
  { to: "/diagnostico", n: "12", t: "Diagnóstico por Problema", d: "Selecione o sintoma e veja os serviços indicados para o seu caso.", I: Gauge },
  { to: "/pacotes", n: "02", t: "Pacotes Completos", d: "De para-choque a para-choque e isolamento termoacústico completo.", I: Package },
  { to: "/dr-ruidos", n: "DR", t: "Dr. Ruídos", d: "Barulho difícil de encontrar? Envie áudio, vídeo e fotos e agende a inspeção.", I: Stethoscope, wide: true },
];

const Hero = () => {
  const { config } = useApp();
  const vid = config?.branding?.vehicle_file_id;
  const img = vid ? fileUrl(vid) : "/img/carro-desmontado.jpg";
  return (
    <section className="relative overflow-hidden border-b border-slate-800 md:min-h-[88vh]" data-testid="home-hero">
      <img src={img} alt="Veículo desmontado para tratamento técnico" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-vehicle-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/85 to-[#0B0F17]/30" />
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="relative mx-auto flex max-w-7xl flex-col justify-center px-4 py-12 sm:px-6 md:min-h-[88vh] md:py-20">
        <p className="eyebrow fade-up"><span className="h-px w-8 bg-red-500" />{BRAND.frase}</p>
        <h1 data-testid="hero-headline" className="fade-up mt-5 font-heading text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-8xl" style={{ animationDelay: "80ms" }}>
          <span className="metal-text">Nillo</span> <span className="text-[#DC2626]">Ruídos</span>
        </h1>
        <p className="fade-up mt-5 max-w-xl text-lg font-semibold uppercase tracking-wide text-white md:text-xl" style={{ animationDelay: "160ms" }}>
          Tratamento de ruídos e isolamento termoacústico
        </p>
        <p className="fade-up mt-2 text-slate-400" style={{ animationDelay: "220ms" }}>Veículos, geradores e embarcações.</p>
        <div className="fade-up mt-8 flex flex-wrap gap-3 md:mt-10" style={{ animationDelay: "300ms" }}>
          <a href="#servicos" className="btn-red md:hidden" data-testid="cta-ver-servicos">Ver serviços <ArrowRight className="h-4 w-4" /></a>
          <Link to="/orcamento" className="btn-red" data-testid="cta-orcamento-wizard">Solicitar orçamento <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/diagnostico" className="btn-line" data-testid="cta-diagnostico">Diagnosticar ruído</Link>
          <a href={waLink("Olá, Nillo Ruídos! Gostaria de falar sobre o tratamento do meu veículo.")} target="_blank" rel="noreferrer" className="btn-line" data-testid="cta-whatsapp">
            <WhatsAppIcon className="h-4 w-4" /> Falar no WhatsApp
          </a>
        </div>
        <p className="mt-6 font-mono text-sm text-slate-300">WhatsApp <b className="text-white">{BRAND.telefone}</b></p>
      </div>
    </section>
  );
};

const Destaques = () => (
  <section className="border-b border-slate-800 bg-[#111723]" data-testid="home-destaques">
    <div className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4">
      {DESTAQUES.map((d, i) => {
        const I = ICONS[i];
        return (
          <div key={d} className="flex items-center gap-3 border-slate-800 px-4 py-6 sm:px-6 [&:not(:last-child)]:border-r">
            <I className="h-6 w-6 shrink-0 text-red-500" />
            <span className="font-heading text-sm font-bold uppercase tracking-wide text-white">{d}</span>
          </div>
        );
      })}
    </div>
  </section>
);

const Areas = () => (
  <section id="servicos" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-12 sm:px-6 md:py-24" data-testid="home-servicos">
    <SectionTitle eyebrow="Engenharia do silêncio" title="Nossos serviços" sub="Todas as opções são numeradas. Selecione uma ou várias e o sistema monta a descrição operacional da proposta automaticamente." />
    <div className="grid grid-cols-2 gap-px bg-slate-800 lg:grid-cols-5">
      {AREAS.map(({ to, n, t, d, I, wide }) => (
        <Link key={to} to={to} data-testid={`home-area-${to.slice(1)}`} className={`group flex flex-col bg-[#0B0F17] p-4 transition-colors duration-300 hover:bg-[#111723] active:bg-[#161E2E] sm:p-7 ${wide ? "col-span-2 lg:col-span-1" : ""}`}>
          <div className="flex items-center justify-between">
            <I className="h-7 w-7 text-red-500" />
            <span className="font-mono text-3xl font-bold text-slate-700 transition-colors group-hover:text-red-500">{n}</span>
          </div>
          <h3 className="mt-5 text-base font-bold uppercase leading-tight text-white sm:mt-10 sm:text-xl">{t}</h3>
          <p className="mt-2 text-xs leading-relaxed text-slate-400 sm:text-sm">{d}</p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-red-400 group-hover:text-red-300">Ver opções <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
    </div>
  </section>
);

const Tecnologia = () => (
  <section className="border-y border-slate-800 bg-[#111723]">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2">
      <div>
        <SectionTitle eyebrow="Materiais" title={MATERIAIS.titulo} sub={MATERIAIS.descricao} />
        <div className="grid gap-3 sm:grid-cols-2">
          {MATERIAIS.nao_utilizados.map((m) => (
            <div key={m} className="flex items-center gap-3 border border-slate-700 bg-black/30 p-4">
              <ShieldCheck className="h-5 w-5 text-red-500" />
              <span className="text-sm text-slate-200">Não utilizamos: <b>{m.toLowerCase()}</b></span>
            </div>
          ))}
        </div>
        <Link to="/informativos" className="btn-line mt-8" data-testid="home-informativos-link">Informações técnicas</Link>
      </div>
      <div className="relative flex flex-col justify-center border border-red-900/50 bg-[#0B0F17] p-10">
        <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Redução média dos ruídos existentes</span>
        <p className="mt-3 font-heading text-7xl font-bold text-white sm:text-8xl">40<span className="text-[#DC2626]">–</span>70<span className="text-3xl text-red-500">%</span></p>
        <p className="mt-4 text-sm leading-relaxed text-slate-400">{RESULTADO}</p>
      </div>
    </div>
  </section>
);

const MissaoVisao = () => (
  <section className="mx-auto grid max-w-7xl gap-6 px-4 py-24 sm:px-6 md:grid-cols-2">
    {[["Missão", MISSAO], ["Visão", VISAO]].map(([t, d]) => (
      <div key={t} className="card-tech p-8">
        <p className="eyebrow">{t}</p>
        <p className="mt-4 text-lg leading-relaxed text-slate-200">{d}</p>
      </div>
    ))}
  </section>
);

const ChamadaFinal = () => (
  <section className="relative overflow-hidden bg-[#DC2626]" data-testid="home-final-cta">
    <div className="absolute inset-0 grid-bg opacity-40" />
    <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 py-20 sm:px-6 md:flex-row md:items-center">
      <div>
        <h2 className="font-heading text-4xl font-bold uppercase text-white sm:text-5xl">Seu carro novo, de novo.</h2>
        <p className="mt-3 text-lg text-red-100">Mais silêncio. Mais conforto. Menos vibrações.</p>
        <p className="mt-2 font-mono text-sm text-white">WhatsApp {BRAND.telefone} · Nillo Ruídos — Excelência em conforto.</p>
      </div>
      <Link to="/orcamento" data-testid="final-cta-contrate" className="inline-flex items-center gap-2 bg-black px-8 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-slate-900">
        Contrate agora <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Areas />
      <Destaques />
      <Tecnologia />
      <MissaoVisao />
      <ChamadaFinal />
    </>
  );
}
